import { Component, OnInit, Input } from '@angular/core';
import { Router, NavigationStart } from "@angular/router";
import { DatePipe } from "@angular/common";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { combineLatest, Subscription} from "rxjs";
import { tap, filter } from "rxjs/operators";

import { GISPoint, GISPointTypes, Well, WellPermittingModifiers, WellWithFinancialAssurance } from '../../../../../common/models/well.model';
import { WellDirection } from '../../../../../form-controls/components/new-casing-plan/models/casing-plan.model';
import { Organization } from '../../../../../common/models/organization.model';
import { FlaggedComponent } from '../../../data-table/columns/flagged/flagged.component';
import { RouterLinkRendererComponent } from "../../../../../common/components/ag-grid/router-link-renderer.component";
import { CheckboxRendererComponent } from "../../../../../common/components/ag-grid/checkbox-renderer.component";
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from '../../../ag-grid/filters/boolean-filter.component';
import { Issue, IssuesConfig } from '../../../../models/issue.model';
import { GridFormatterService } from '../../../../services/grid-formatter.service';
import { DomainDefinitionService } from '../../../../../builder/services/domain-definition.service';
import { AuthService } from "../../../../../services/auth.service";

import { DomainValue } from '../../../../../models/domain.model';

import { dateComparator } from '../../../data-table/comparators/data-table.comparators';



@Component({
    selector: 'app-organization-wells-grid',
    templateUrl: './organization-wells-grid.component.html',
    styleUrls: ['./organization-wells-grid.component.scss'],
    standalone: false
})
export class OrganizationWellsGridComponent implements OnInit {

    @Input() gridAriaLabel: string;
	api?: GridApi;

	get dataReady(): boolean {
		return this._domainsLoaded;;
	}

	private _domainsLoaded: boolean = false;

	_booleanfilterParams = { isInServerSideFilterMode: false, } as IBooleanFilterParams;
	_buyerFAColumn: ColDef;
	_wellHasIssueColumn: ColDef;
	_columns: ColDef[];
	config: IssuesConfig;

	private _wells: WellWithFinancialAssurance[] = [];

	get data(): WellWithFinancialAssurance[] {
		return this._wells;
	}

	@Input() set data(value: WellWithFinancialAssurance[]) {
		this.setData(value);
	}

	@Input() organization: Organization;
	@Input() wellIssues: Issue[];
	dataSubscriptions: Subscription = new Subscription();
	public gridOptions: GridOptions;

	_allWellTypeDomainValues: DomainValue[];
	_allWellTypeDescriptionDomainValues: DomainValue[];
	_allWellDispositionDomainValues: DomainValue[];
	_allWellDispositionLegacyTrackingValues: DomainValue[];
	_formations: DomainValue[];

	get excelStyles(): any[] {
		if (this._gridFormatterService) return this._gridFormatterService.excelStyles;
		return [];
	}

	constructor(private _gridFormatterService: GridFormatterService, private _router: Router, private _domainService: DomainDefinitionService, private datePipe: DatePipe, private _auth: AuthService) {
		this.config = (window as any)["issuesConfig"];

	}

	ngOnInit() {
		this.gridOptions = this._gridFormatterService.BuildDefaultGridOptionsWithColumns(this.buildColumns());
		this.gridOptions.onGridReady = (params) => {
			this.api = params.api;
		};
		this.gridOptions.headerHeight = 50;
		if (this.data == null || this.data.length == 0) {
			this.showNoRows();
		}
		let typeObs$ = this._domainService.getValues("WELL_TYP").pipe(tap((domains: DomainValue[]) => {
			if (domains) this._allWellTypeDomainValues = domains;
		}));

		let descObs$ = this._domainService.getValues("WLTYP_DESC").pipe(tap((domains: DomainValue[]) => {
			if (domains) this._allWellTypeDescriptionDomainValues = domains;
		}));

		let statusObs$ = this._domainService.getValues("STATUS_DESC").pipe(tap((domains: DomainValue[]) => {
			if (domains) this._allWellDispositionDomainValues = domains;
		}));

		let reasonObs$ = this._domainService.getValues("STATUS_REAS").pipe(tap((domains: DomainValue[]) => {
			if (domains) this._allWellDispositionLegacyTrackingValues = domains;
		}));

		let formationsObs$ = this._domainService.getValues("FORMATION").pipe(tap((domains: DomainValue[]) => {
			if (domains) this._formations = domains;
		}));

		let dataSubs$ = combineLatest([typeObs$, descObs$, statusObs$, reasonObs$, formationsObs$]).pipe(
		).subscribe({
			next: () => {
				this._domainsLoaded = true;
			}, 
			error: (error) => { console.error(error); }
		});

		this.dataSubscriptions.add(dataSubs$);

		this._router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
			this.dataSubscriptions.unsubscribe();
		});
	}

	addBuyerFAColumn(): void {
		let columnExists = this._columns.findIndex(f => f.field == this._buyerFAColumn.field);
		if (columnExists == -1) {
			this.addColumnToEnd(this._buyerFAColumn);
		}		
	}

	addColumnToEnd(columnToPush: ColDef) {
		this._gridFormatterService.applyFilterKeyboardHintTooltips([columnToPush]);
		this._columns.push(columnToPush);
		if (this.api != null) {
			this.api.setGridOption("columnDefs", this._columns);
			this.api.sizeColumnsToFit();
		}				
	}

	addIssuesColumn(): void {
		let columnExists = this._columns.findIndex(f => f.headerName == this._wellHasIssueColumn.headerName); //would have preferred to use field, but there's more than one column with the same field definition & not sure why the issues column is using that field
		if (columnExists == -1) {
			this.addColumnToEnd(this._wellHasIssueColumn);
		}		
	}

	buildColumns() : ColDef[] {
		this._columns = [
			{
				cellRenderer: RouterLinkRendererComponent,
				cellRendererParams: {
					openInNewWindow: true,
					textField: 'id',
					url: (this._auth.User.isInternal)
						? (data: Well) => { return `/common/organization/${data.organizationId}/well/${data.id}`; }
						: (data: Well) => { return `/dashboard/view/well/${data.id}`}
				},
				headerClass: "wrapped",
				headerName: 'API #',
				headerTooltip: 'API #',
				field: 'id',
				tooltipField: 'id',
				filter: "agTextColumnFilter",
				filterParams: this._gridFormatterService.defaultTextFilterParams,
				minWidth: 130,
				cellClass: "stringType"
			},
			{
				headerName: 'Well Name',
				headerTooltip: 'Well Name',
				headerClass: "wrapped",
				valueGetter: this.getLeaseName(),
				tooltipValueGetter: this.getLeaseName(),
				filter: "agTextColumnFilter",
				filterParams: this._gridFormatterService.defaultTextFilterParams,
				minWidth: 150,
			},
			{
				headerName: 'Well Number',
				headerTooltip: 'Well Number',
				headerClass: "wrapped",
				field: 'wellNumber',
				tooltipField: 'wellNumber',
				filter: "agTextColumnFilter",
				filterParams: this._gridFormatterService.defaultTextFilterParams,
				minWidth: 140,
			},
			{
				headerName: 'Well Disposition',
				headerTooltip: 'Well Disposition',
				headerClass: "wrapped",
				valueGetter: this.formatWellDomainFieldsType('wellStatus.disposition'),
				tooltipValueGetter: this.formatWellDomainFieldsType('wellStatus.disposition'),
				minWidth: 130,
			},
			{
				headerName: 'Legacy Tracking',
				headerTooltip: 'Legacy Tracking',
				headerClass: "wrapped",
				valueGetter: this.formatWellDomainFieldsType('wellStatus.legacyTracking'),
				tooltipValueGetter: this.formatWellDomainFieldsType('wellStatus.legacyTracking'),
				minWidth: 130,
			},
			{
				headerName: 'Owner Name',
				headerTooltip: 'Owner Name',
				headerClass: "wrapped",
				field: 'organizationName',
				tooltipField: 'organizationName',
				filter: "agTextColumnFilter",
				filterParams: this._gridFormatterService.defaultTextFilterParams,
				minWidth: 150,
			},
			{
				headerName: 'Owner Number',
				headerTooltip: 'Owner Number',
				headerClass: "wrapped",
				field: 'organizationId',
				tooltipField: 'organizationId',
				filter: "agTextColumnFilter",
				filterParams: this._gridFormatterService.defaultTextFilterParams,
				minWidth: 145,
			},
			{
				headerName: 'County Name',
				headerTooltip: 'County Name',
				headerClass: "wrapped",
				field: 'location.gisPoints',
				tooltipValueGetter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getCountyName, null, "N/A", true),
				valueFormatter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getCountyName, null, "N/A", true),
				valueGetter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getCountyName, null, "N/A", true),
				filter: "agTextColumnFilter",
				filterParams: this._gridFormatterService.defaultTextFilterParams,
				minWidth: 135,
			},
			{
				headerName: 'Township  Name',
				headerTooltip: 'Township  Name',
				headerClass: "wrapped",
				field: 'location.gisPoints',
				tooltipValueGetter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getTownshipName, null, "N/A", true),
				valueFormatter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getTownshipName, null, "N/A", true),
				valueGetter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getTownshipName, null, "N/A", true),
				filter: "agTextColumnFilter",
				filterParams: this._gridFormatterService.defaultTextFilterParams,
				minWidth: 150,
			},
			{
				headerName: 'Tract < 5 Acres',
				headerTooltip: 'Tract < 5 Acres',
				headerClass: "wrapped",
				valueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'WithoutFiveAcres'),
				tooltipValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'WithoutFiveAcres'),
				filter: AgGridBooleanFilterComponent,
				filterParams: this._booleanfilterParams,
				filterValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'WithoutFiveAcres', true),
				minWidth: 150,
			},
			{
				headerName: 'Release of Leases',
				headerTooltip: 'Release of Leases',
				headerClass: "wrapped",
				valueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ReleaseOfLease'),
				tooltipValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ReleaseOfLease'),
				filter: AgGridBooleanFilterComponent,
				filterParams: this._booleanfilterParams,
				filterValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ReleaseOfLease', true),
				minWidth: 160,
			},
			{
				headerName: 'Permitted Urban',
				headerTooltip: 'Permitted Urban',
				headerClass: "wrapped",
				valueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ConsideredUrbanForPermitting'),
				tooltipValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ConsideredUrbanForPermitting'),
				filter: AgGridBooleanFilterComponent,
				filterParams: this._booleanfilterParams,
				filterValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ConsideredUrbanForPermitting', true),
				minWidth: 150,
			},
			//{
			//	headerName: 'Urban',
			//	headerTooltip: 'Urban',
			//	headerClass: "wrapped",
			//	field: 'location.isUrban',
			//	valueFormatter: this._gridFormatterService.formatBooleanToYesNo('location.isUrban'),
			//	tooltipValueGetter: this._gridFormatterService.formatBooleanToYesNo('location.isUrban'),
			//	filter: "booleanFilter",
			//	filterParams: this._booleanfilterParams,
			//	minWidth: 100,
			//},
			{
				headerName: 'Horizontal',
				headerTooltip: 'Horizontal',
				headerClass: "wrapped",
				field: 'location.slant',
				valueFormatter: this.isHorizontal(),
				tooltipValueGetter: this.isHorizontal(),
				filter: AgGridBooleanFilterComponent,
				filterParams: this._booleanfilterParams,
				filterValueGetter: this.isHorizontal(true),
				minWidth: 125,
			},
			{
				headerName: 'Slant',
				headerTooltip: 'Slant',
				headerClass: "wrapped",
				field: 'location.slant',
				valueFormatter: this.formatWellSlant(),
				tooltipValueGetter: this.formatWellSlant(),
				minWidth: 100,
			},
			{
				headerName: "Drilling Unit Acres",
				headerTooltip: "Drilling Unit Acres",
				headerClass: "wrapped",
				field: "acresInDrillingUnit",
				valueFormatter: this._gridFormatterService.formatNumberPrecision("acresInDrillingUnit", 6, "N/A"),
				tooltipValueGetter: this._gridFormatterService.formatNumberPrecision("acresInDrillingUnit", 6, "N/A"),
				filter: "agNumberColumnFilter",
				filterParams: this._gridFormatterService.defaultNumberFilterParams,
				minWidth: 135
			},
			{
				headerName: 'Well Type',
				headerTooltip: 'Well Type',
				headerClass: "wrapped",
				valueGetter: this.formatWellDomainFieldsType('wellStatus.type.type'),
				tooltipValueGetter: this.formatWellDomainFieldsType('wellStatus.type.type'),
				minWidth: 115,
			},
			{
				headerName: "H2S Area",
				headerTooltip: "H2S Area",
				headerClass: "wrapped",
				field: "h2sGasPresent",
				cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "h2sGasPresent",
                    description: "H2S Area",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
				tooltipValueGetter: this._gridFormatterService.formatBooleanToYesNo("h2sGasPresent"),
				filter: AgGridBooleanFilterComponent,
				filterParams: { falseValueDisplayFormat: 'No', trueValueDisplayFormat: 'Yes', isInServerSideFilterMode: false, fieldPath: "h2sGasPresent" } as IBooleanFilterParams,
				minWidth: 125
			},
			{
				headerName: 'Well Type Description',
				headerTooltip: 'Well Type Description',
				headerClass: "wrapped",
				valueGetter: this.formatWellDomainFieldsType('wellStatus.type.description'),
				tooltipValueGetter: this.formatWellDomainFieldsType('wellStatus.type.description'),
				minWidth: 165,
			},
			{
				headerName: 'Target Formation',
				headerTooltip: 'Target Formation',
				headerClass: "wrapped",
				field: 'proposedFormation',
				valueGetter: this.formatWellDomainFieldsType('proposedFormation'),
				tooltipValueGetter: this.formatWellDomainFieldsType('proposedFormation'),
				minWidth: 155,
			},
			{
				headerName: 'Deepest Formation',
				headerTooltip: 'Deepest Formation',
				headerClass: "wrapped",
				field: 'deepestFormationPenetrated',
				valueGetter: this.formatWellDomainFieldsType('deepestFormationPenetrated'),
				tooltipValueGetter: this.formatWellDomainFieldsType('deepestFormationPenetrated'),
				minWidth: 165,
			},
			{
				headerName: 'Date Spud',
				headerTooltip: 'Date Spud',
				headerClass: "wrapped",
				field: 'dateOfOriginalSpud',
				tooltipValueGetter: this._gridFormatterService.formatDate("dateOfOriginalSpud", "MM/dd/yyyy", 'N/A', '+0000'),
				valueFormatter: this._gridFormatterService.formatDate("dateOfOriginalSpud", "MM/dd/yyyy", 'N/A', '+0000'),
				comparator: dateComparator,
				filter: "agDateColumnFilter",
				filterParams: this._gridFormatterService.defaultDateFilterParams,
				minWidth: 155,
			},
			{
				headerName: 'Date T.D.',
				headerTooltip: 'Date T.D.',
				headerClass: "wrapped",
				field: 'dateTotalDepthReached',
				tooltipValueGetter: this._gridFormatterService.formatDate("dateTotalDepthReached", "MM/dd/yyyy", 'N/A', '+0000'),
				valueGetter: this._gridFormatterService.formatDate("dateTotalDepthReached", "MM/dd/yyyy", 'N/A', '+0000'),
				comparator: dateComparator,
				filter: "agDateColumnFilter",
				filterParams: this._gridFormatterService.defaultDateFilterParams,
				minWidth: 155,
			},
			{
				headerName: 'Plugback Date',
				headerTooltip: 'Plugback Date',
				headerClass: 'wrapped',
				field: 'plugging.dateOfPlugBack',
				tooltipValueGetter: this._gridFormatterService.formatDate('plugging.dateOfPlugBack', "MM/dd/yyyy", "N/A", "+0000"),
				valueGetter: this._gridFormatterService.formatDate('plugging.dateOfPlugBack', 'MM/dd/yyyy', 'N/A', '+0000'),
				comparator: dateComparator,
				filter: "agDateColumnFilter",
				filterParams: this._gridFormatterService.defaultDateFilterParams,
				minWidth: 155
			},
			{
				headerName: 'Prod Date',
				headerTooltip: 'Prod Date',
				headerClass: 'wrapped',
				field: 'production.dateOfProduction',
				tooltipValueGetter: this._gridFormatterService.formatDate('production.dateOfProduction', "MM/dd/yyyy", "N/A", "+0000"),
				valueGetter: this._gridFormatterService.formatDate('production.dateOfProduction', 'MM/dd/yyyy', 'N/A', '+0000'),
				comparator: dateComparator,
				filter: "agDateColumnFilter",
				filterParams: this._gridFormatterService.defaultDateFilterParams,
				minWidth: 155
			},
			{
				headerName: 'Date P&A',
				headerTooltip: 'Date P&A',
				headerClass: "wrapped",
				field: 'plugging.datePluggedAndAbandoned',
				tooltipValueGetter: this._gridFormatterService.formatDate("plugging.datePluggedAndAbandoned", "MM/dd/yyyy", 'N/A', '+0000'),
				valueGetter: this._gridFormatterService.formatDate("plugging.datePluggedAndAbandoned", "MM/dd/yyyy", "N/A", "+0000"),
				comparator: dateComparator,
				filter: "agDateColumnFilter",
				filterParams: this._gridFormatterService.defaultDateFilterParams,
				minWidth: 155,
			},
			{
				headerName: "Vaulted / Vented",
				headerTooltip: "Vaulted / Vented",
				headerClass: "wrapped",
				field: "vaultedVented",
				tooltipValueGetter: this._gridFormatterService.formatBooleanToYesNo("vaultedVented", "No"),
				cellRenderer: CheckboxRendererComponent,
				cellRendererParams: {
					action: () => { return false; },
					field: "vaultedVented",
					description: "Vaulted / Vented",
					isVisible: () => { return true; },
					isEnabled: () => { return false; }
				},
				editable: false,
				filter: AgGridBooleanFilterComponent,
				filterParams: { falseValueDisplayFormat: 'No', trueValueDisplayFormat: 'Yes', isInServerSideFilterMode: false, fieldPath: "vaultedVented", treatNullValuesAsFalse: true } as IBooleanFilterParams,
				minWidth: 125
			},
			{
				headerName: "Plug Report Received From Operator",
				headerTooltip: "Plug Report Received From Operator",
				headerClass: "wrapped",
				field: "plugReportReceivedFromOperator",
				valueFormatter: this._gridFormatterService.formatBooleanToYesNo("plugReportReceivedFromOperator"),
				tooltipValueGetter: this._gridFormatterService.formatBooleanToYesNo("plugReportReceivedFromOperator"),
				filter: AgGridBooleanFilterComponent,
				filterParams: { falseValueDisplayFormat: 'No', trueValueDisplayFormat: 'Yes', isInServerSideFilterMode: false, fieldPath: "plugReportReceivedFromOperator" } as IBooleanFilterParams,
				minWidth: 160
			},
			{
				headerName: 'Driller T.D.',
				headerTooltip: 'Driller T.D.',
				headerClass: "wrapped",
				field: 'depths.drillersMeasuredVerticalDepth',
				tooltipField: 'depths.drillersMeasuredVerticalDepth',
				filter: "agNumberColumnFilter",
				filterParams: this._gridFormatterService.defaultNumberFilterParams,
				minWidth: 120,
			},
			{
				headerName: 'Logger T.D.',
				headerTooltip: 'Logger T.D.',
				headerClass: "wrapped",
				field: 'depths.loggerMeasuredVerticalDepth',
				tooltipField: 'depths.loggerMeasuredVerticalDepth',
				filter: "agNumberColumnFilter",
				filterParams: this._gridFormatterService.defaultNumberFilterParams,
				minWidth: 120,
			},
			{
				headerName: "Wellhead Latitude",
				headerTooltip: "Wellhead Latitude",
				headerClass: "wrapped",
				valueGetter: this._gridFormatterService.formatWellCoordinateValue(GISPointTypes.WellHead, "latitude83", "N/A"),
				tooltipValueGetter: this._gridFormatterService.formatWellCoordinateValue(GISPointTypes.WellHead, "latitude83", "N/A"),
				filter: "agNumberColumnFilter",
				filterParams: this._gridFormatterService.defaultNumberFilterParams,
				minWidth: 130
			},
			{
				headerName: "Wellhead Longitude",
				headerTooltip: "Wellhead Longitude",
				headerClass: "wrapped",
				valueGetter: this._gridFormatterService.formatWellCoordinateValue(GISPointTypes.WellHead, "longitude83", "N/A"),
				tooltipValueGetter: this._gridFormatterService.formatWellCoordinateValue(GISPointTypes.WellHead, "longitude83", "N/A"),
				filter: "agNumberColumnFilter",
				filterParams: this._gridFormatterService.defaultNumberFilterParams,
				minWidth: 130
			},
			{
				headerName: "Requires Bonding",
				headerTooltip: "Requires Bonding",
				headerClass: "wrapped",
				field: "requiresBonding",
				tooltipValueGetter: this._gridFormatterService.formatBooleanToYesNo("requiresBonding"),
				filterValueGetter: this._gridFormatterService.formatBooleanToYesNo("requiresBonding"),
				cellRenderer: CheckboxRendererComponent,
				cellRendererParams: {
					action: () => { return false; },
					field: "requiresBonding",
					description: "Requires Bonding",
					isVisible: () => { return true; },
					isEnabled: () => { return false; }
				},
				editable: false,
				suppressMovable: true,
				minWidth: 125
			} as ColDef,
			{
				headerName: "Requires Insurance",
				headerTooltip: "Requires Insurance",
				headerClass: "wrapped",
				field: "requiresInsurance",
				tooltipValueGetter: this._gridFormatterService.formatBooleanToYesNo("requiresInsurance"),
				filterValueGetter: this._gridFormatterService.formatBooleanToYesNo("requiresInsurance"),
				cellRenderer: CheckboxRendererComponent,
				cellRendererParams: {
					action: () => { return false; },
					field: "requiresInsurance",
					description: "Requires Insurance",
					isVisible: () => { return true; },
					isEnabled: () => { return false; }
				},
				editable: false,
				suppressMovable: true,
				minWidth: 125
			} as ColDef
		] as ColDef[];

		this._buyerFAColumn = {
			headerName: 'Buyer FA',
			headerClass: "wrapped",
			field: 'faOrganizationId',
			printable: false,
			cellRenderer: FlaggedComponent,
			cellRendererParams: { values: 'icon-blocked' },
			valueFormatter: this.buyerLacksFA(),
			tooltipValueGetter: this.buyerLacksFA(true),
			filter: AgGridBooleanFilterComponent,
			filterParams: this._booleanfilterParams,
			filterValueGetter: this.buyerLacksFA(),
			minWidth: 125,
		} as ColDef;

		this._wellHasIssueColumn = {
			headerName: 'Open Issue',
			headerClass: "wrapped",
			field: 'id',
			cellRenderer: FlaggedComponent,
			cellRendererParams: { values: 'icon-alert' },
			valueGetter: this.hasIssues(),
			tooltipValueGetter: this.hasIssues(true),
			valueFormatter: this.hasIssues(),
			filter: AgGridBooleanFilterComponent,
			filterParams: this._booleanfilterParams,
			filterValueGetter: this.hasIssues(),
			minWidth: 125,
		} as ColDef;

		return this._columns;
	}

	getCountyName(array: GISPoint[]) {
		let pt = array.find(x => x.pointType === GISPointTypes.WellHead);
		return (pt && pt.county) ? pt.county.name : '';
	}

	getLeaseName(): any {
		return (obj: any) => {
			let value = "";
			if (obj.data) {
				if (obj.data["lease"] != null && obj.data["lease"]["name"] != null) value = obj.data["lease"]["name"];
				if ((value == null || value.length == 0) && obj.data["name"] != null) {
					value = obj.data["name"];
					if (obj.data["wellNumber"] != null) {
						let wellNumber = obj.data["wellNumber"].toString().trim();
						if (value.endsWith(wellNumber)) value = value.replace(wellNumber, "");
					}
				}
			}
			return value;
		}
	}

	getTownshipName(array: GISPoint[]) {
		let pt = array.find(x => x.pointType === GISPointTypes.WellHead);
		return (pt && pt.township) ? pt.township.name : '';
	}

	isHorizontal(returnAsBool: boolean = false): any {
		return (obj: any) => {
			if (obj.data) {
				var path = "$.location.slant";
				let fieldValue = this._gridFormatterService.tryGetValue(obj.data, path);

				if (!returnAsBool)
					return fieldValue == "Horizontal" ? "Yes" : "No";
				else
					return fieldValue == "Horizontal";
			}
		}
	}

	hasIssues(returnTooltip: boolean = false): any {
		return (obj: any) => {
			let hasOpenIssue = false;
			let matchingWellIssues: Issue[];
			if (obj && obj.data && obj.data.id) {
				matchingWellIssues = this.wellIssues.filter(a => a.wellId == obj.data.id);
				if (matchingWellIssues.length > 0)
					hasOpenIssue = true;
			}

			if (returnTooltip)
				return hasOpenIssue ? "Well has open Issues : " + matchingWellIssues[0].description : "Well has no open issues";
			else
				return hasOpenIssue ? true : " ";
		}
	}

	buyerLacksFA(returnTooltip: boolean = false): any {
		return (obj: any) => {
			let buyerLacksFa = false;

			if (obj.data && obj.data.faOrganizationId != null && obj.data.faOrganizationId != undefined && obj.data.faOrganizationId != "")
				buyerLacksFa = true;

			if (returnTooltip)
				return buyerLacksFa ? "Buyer Lacks Necessary FA" : "Buyer Has Necessary FA";
			else
				return buyerLacksFa;
		}
	}

	private capitalizeWords(word: string, delimiter: string): string {

		let capitalizedWordParts: string[] = [];
		if (word != undefined && word.indexOf(delimiter) !== -1) {
			let wordParts = word.split(delimiter);
			for (let i = 0; i < wordParts.length; i++) {
				let capitalizedWord = this.capitalizeWord(wordParts[i]);
				capitalizedWordParts.push(capitalizedWord);
			}
		}
		else {
			if (word != undefined) {
				let capitalizedWord = this.capitalizeWord(word);
				capitalizedWordParts.push(capitalizedWord);
			}
		}
		return capitalizedWordParts.join(" ");
	}

	private capitalizeWord(word: string): string {
		let capitalizedWord = "";
		if (word != undefined) {
			capitalizedWord = word.toUpperCase();
		}

		return capitalizedWord;
	}

	exportWells(): void {
		if (this.api != null) {
			const now = this.datePipe.transform(new Date(), "yyyy-MM-dd")?.toString() ?? "";
			let fileName = 'Wells_';
			if (this.organization != null) {
				fileName = `${fileName}${this.organization.name}`;
}
			fileName = `${fileName}_${now}`;
			this._gridFormatterService.exportDataToExcel(fileName, 'Wells', this.api, false);
		}		
	}

	formatWellSlant() {
		return (obj) => {
			if (obj.data) {
				let slantCode = '';
				var path = "$.location.slant";
				let fieldValue = this._gridFormatterService.tryGetValue(obj.data, path);

				switch (fieldValue) {
					case WellDirection.Directional:
						slantCode = "D";
						break;
					case WellDirection.Vertical:
						slantCode = "V";
						break;
					case WellDirection.OtherHorizontal:
						slantCode = "V";
						break;
					default:
						slantCode = "H";
						break;
				};

				return slantCode;
			}
		}
	}

	formatWellDomainFieldsType(field: string) {
		return (obj: any) => {
			if (obj.data) {
				var path = "$." + field;
				let fieldValue = this._gridFormatterService.tryGetValue(obj.data, path);
				let domainValues: DomainValue[] | null = null;
				switch (field) {
					case 'wellStatus.disposition':
						domainValues = this._allWellDispositionDomainValues;
						break;
					case 'wellStatus.legacyTracking':
						domainValues = this._allWellDispositionLegacyTrackingValues;
						break;
					case 'wellStatus.type.type':
						domainValues = this._allWellTypeDomainValues;
						break;
					case 'wellStatus.type.description':
						domainValues = this._allWellTypeDescriptionDomainValues;
						break;
					case 'proposedFormation':
					case 'deepestFormationPenetrated':
						domainValues = this._formations;
						break;
				}

				if (domainValues != null && domainValues.length > 0) {

					let matchingDomainValue = domainValues.filter(a => a.Code == fieldValue);
					if (matchingDomainValue.length > 0) {
						let domainValueToReturn = matchingDomainValue[0].Definition;
						if (field.indexOf("Formation") !== -1) {
							domainValueToReturn = this.capitalizeWord(matchingDomainValue[0].Definition);
						}
						return domainValueToReturn;
					}
					else
						if (field.indexOf("Formation") !== -1) {
							fieldValue = this.capitalizeWords(fieldValue, " ");
							fieldValue = this.capitalizeWords(fieldValue, "/");
						}
					return fieldValue;
				}
				else {
					if (field.indexOf("Formation") !== -1) {
						fieldValue = this.capitalizeWords(fieldValue, " ");
						fieldValue = this.capitalizeWords(fieldValue, "/");
					}
					return fieldValue;
				}
			}
			else return "";
		}
	}

	setData(rows: any): void {
		this._wells = rows ?? [];
		if (this.api) {
			if (this.data.length > 0) {
				var lackingBuyerFA: boolean = false;
				if (this._auth.User.isInternal) { //if Internal, show if not in context of another organization (well search) or if the organization matches the faOrganizationId on any of the wells
					lackingBuyerFA = this.data.filter(a => a.faOrganizationId && (this.organization == null || a.faOrganizationId == this.organization.id || a.faOrganizationId == this.organization.historicalOwnerNumber)).length > 0;
				} else {
					//if external, show the column if the logged in User's organization or rbdms owner number match any of the wells
					lackingBuyerFA = this.data.filter(a => a.faOrganizationId && (a.faOrganizationId == this._auth.User.organizationId || a.faOrganizationId == this._auth.User.rbdmsOwnerNumber)).length > 0;
				}
				if (lackingBuyerFA) {
					this.addBuyerFAColumn();
				}
			}
			this.api.setGridOption("rowData", this.data);
			this.api.setGridOption("loading", false);
		}
	}

	showLoading(): void {
		if (this.api) {
			this.api.setGridOption("loading", true);
		}
	}

	showNoRows(): void {
		if (this.api) {
			this.api.setGridOption('activeOverlay', 'agNoRowsOverlay');
		}
	}
}
